<template>
  <div class="round-icon">
    <i class="bi bi-receipt" />
  </div>
  <div class="title-block" :v-text="$t('app.modals.confirmAssessmentActivation.title')" />
  <p>{{ $t("app.modals.confirmAssessmentActivation.reminder") }}</p>
  <div class="row">
    <p>{{ $t("app.modals.confirmAssessmentActivation.currentBalance") }}</p>
    <p v-text="state.creditAtTheMoment.available" />
  </div>
  <div class="row">
    <p>{{ $t("app.modals.confirmAssessmentActivation.talentsToActivate") }}</p>
    <p v-text="data.length" />
  </div>
  <div class="last row">
    <p :class="{ 'red-color': prevision < 0 }">
      {{ $t("app.modals.confirmAssessmentActivation.newBalance") }}
    </p>
    <p :class="{ 'red-color': prevision < 0 }" v-text="prevision" />
  </div>
  <Toast v-bind="prevision < 0 ? state.toasts[1] : state.toasts[0]" icon="bi-info-circle" :closable="false" />
  <div class="btn-container">
    <Button class="primary" :label="prevision < 0 ? $t('app.modals.confirmAssessmentActivation.buttons.topup') : $t('app.modals.confirmAssessmentActivation.buttons.confirm')" :loading="state.btnIsLoading" @click="prevision >= 0 ? activateParticipants() : reloadCredits()" />
    <Button class="secondary outline" :label="$t('app.modals.confirmAssessmentActivation.buttons.cancel')" @click="close" />
  </div>
</template>

<script setup lang="ts">
import { useAppStore } from "@/stores/app.js";
import { reactive, computed, watch } from "vue";
import { useRoute } from "vue-router";
import { useToast } from "vue-toastification";
import { getError } from "@/composables/error.js";
import { useActivateParticipations } from "@/queries/participant.query";
import { Participant } from "@/types/participant";
import { useOrganisation, useOrganisationCredits } from "@/queries/organisation.query";
import { Credits } from "@/types/company";
import { useRoles } from "@/queries/user.query";

const route = useRoute();
const emits = defineEmits(["close", "confirm", "confirmBulk"]);
const toast = useToast();
const { t } = useI18n();

const appStore = useAppStore();

const { data: organisation, isPending: isOrganisationPending } = useOrganisation();
const { data: roles } = useRoles();
const { data: credits, isPending: isCreditsPending } = useOrganisationCredits();
const { mutateAsync: activateParticipations } = useActivateParticipations();

const props = defineProps<{
  data: Participant[];
}>();

const state = reactive({
  toasts: [
    {
      title: t("app.modals.confirmAssessmentActivation.warning.yellowTitle"),
      text: t("app.modals.confirmAssessmentActivation.warning.yellow"),
      color: "yellow",
    },
    {
      text: t("app.modals.confirmAssessmentActivation.warning.red"),
      color: "red",
    },
  ],
  creditAtTheMoment: {
    available: 0,
    reserved: 0,
    in_progress: 0,
    consumed: 0,
  } as Credits,
  btnIsLoading: false,
});

const prevision = computed(() => {
  return state.creditAtTheMoment.available - props.data.length;
});

function close() {
  emits("close");

  const item = JSON.parse(localStorage.getItem("pending_activations"));

  if (item && item.campaign_id === route.params.campaignId) {
    localStorage.removeItem("pending_activations");
  }
}

async function activateParticipants() {
  const item = JSON.parse(localStorage.getItem("pending_activations"));

  if (item && item.campaign_id === route.params.campaignId) {
    localStorage.removeItem("pending_activations");
  }

  state.btnIsLoading = true;

  const participant_ids = props.data.map((elt) => elt.participant_id).filter((id) => !!id);

  await activateParticipations({
    context: {
      organisationId: route.params.companyId as string,
      spaceId: route.params.spaceId as string || props.data[0].campaign.campaign.parent_workspace_id,
      campaignId: route.params.campaignId as string || props.data[0].campaign.id,
    },
    participantIds: participant_ids,
  },
  {
    onSuccess: () => {
      toast.success(`Candidat${props.data.length > 1 ? "s" : ""} activé${props.data.length > 1 ? "s" : ""} avec succès.`);
      if (props.data.length > 1) emits("confirmBulk");
      appStore.activatedTableHasNews[`${route.params.campaignId}`] = true;
    },
    onError: (error) => {
      toast.error(getError(error));
    }
  });

  emits("confirm");

  state.btnIsLoading = true;
}

function reloadCredits() {
  toast.info(t('app.contact.contactUsToGetCredits'));
}

watch(
  () => props.data,
  () => {
    if (!isCreditsPending.value) {
      state.creditAtTheMoment = { ...credits.value };
    }
  },
  { immediate: true },
);
</script>
